/**
 * The full search page. **INBOX scope item 22.**
 *
 * ⛔ `search-view.ts` owns the decisions - the five empty states, the chip
 * counts, which queries a chip needs, and which facets exist. This owns the
 * words, the controls and the navigation.
 *
 * ⚠️ IT IS A PAGE AND NOT AN OVERLAY, which is why it has an `idle` state the
 * palette does not: it can be open with nothing typed.
 */
import { useEffect, useState } from 'react'
import {
  type ResultType,
  BUILDABLE_FACETS,
  FACETS_AWAITING_CRM_BUILD,
  MIN_QUERY,
  chipCount,
  contactTarget,
  queriesFor,
  searchEmpty,
} from './search-view.js'
import {
  type AssignableUser,
  type ContactHit,
  type MessageHit,
  fetchAssignableUsers,
  searchContactsApi,
  searchMessagesApi,
} from '../data/inbox-api.js'

export type SearchPageProps = {
  query: string
  onQueryChange: (q: string) => void
  /** A message result: conversation AND message, per `Build_Order:490`. */
  onOpenMessage: (conversationId: string, messageId: string) => void
  onOpenConversation: (conversationId: string) => void
  /** TEST SEAMS. Production passes nothing. */
  searchMessages?: typeof searchMessagesApi
  searchContacts?: typeof searchContactsApi
  loadUsers?: typeof fetchAssignableUsers
}

const TYPE_LABEL: Record<ResultType, string> = {
  both: 'All',
  messages: 'Messages',
  contacts: 'People',
}

export function SearchPage({
  query,
  onQueryChange,
  onOpenMessage,
  onOpenConversation,
  searchMessages = searchMessagesApi,
  searchContacts = searchContactsApi,
  loadUsers = fetchAssignableUsers,
}: SearchPageProps): React.JSX.Element {
  const [type, setType] = useState<ResultType>('both')
  const [channel, setChannel] = useState<string>('')
  const [assigneeId, setAssigneeId] = useState<string>('')
  const [after, setAfter] = useState<string>('')
  const [before, setBefore] = useState<string>('')

  const [messages, setMessages] = useState<MessageHit[]>([])
  const [contacts, setContacts] = useState<ContactHit[]>([])
  const [loadedMessages, setLoadedMessages] = useState(false)
  const [loadedContacts, setLoadedContacts] = useState(false)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<AssignableUser[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    void loadUsers()
      .then((r) => setUsers(r.users))
      .catch(() => {
        /* the assignee facet simply has no options; not worth a banner */
      })
  }, [loadUsers])

  const want = queriesFor({ query, type, facets: {} })

  useEffect(() => {
    const q = query.trim()
    if (q.length < MIN_QUERY) {
      setMessages([])
      setContacts([])
      setLoadedMessages(false)
      setLoadedContacts(false)
      setLoading(false)
      return
    }
    /**
     * ⛔ THE RESPONSE IS DISCARDED IF THE QUERY HAS MOVED ON, the same guard the
     * palette carries and for the same reason: keystrokes race, and an older
     * answer repainting a newer one reads as the page ignoring what was typed.
     */
    let live = true
    setLoading(true)
    setFailure(null)
    const facets = {
      ...(channel !== '' ? { channel } : {}),
      ...(assigneeId !== '' ? { assignee: assigneeId } : {}),
      ...(after !== '' ? { after } : {}),
      ...(before !== '' ? { before } : {}),
    }
    void Promise.allSettled([
      want.messages ? searchMessages(q, facets) : Promise.resolve(null),
      want.contacts ? searchContacts(q) : Promise.resolve(null),
    ])
      .then(([m, c]) => {
        if (!live) return
        /**
         * ⚠️ `allSettled`, NOT `all`, AND THE COUNTS ARE SET INDEPENDENTLY. One
         * query failing must not blank the other's results - the same rule
         * `refetchAll` follows. A rejected half stays UNLOADED rather than
         * loaded-with-zero, so its chip reads unknown rather than "none".
         */
        if (m.status === 'fulfilled' && m.value !== null) {
          setMessages(m.value.messages)
          setLoadedMessages(true)
        } else if (m.status === 'rejected') {
          setFailure('Message search did not answer')
        }
        if (c.status === 'fulfilled' && c.value !== null) {
          setContacts(c.value.contacts)
          setLoadedContacts(true)
        } else if (c.status === 'rejected') {
          setFailure('People search did not answer')
        }
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [query, type, channel, assigneeId, after, before, searchMessages, searchContacts, want.messages, want.contacts])

  const shownMessages = want.messages ? messages : []
  const shownContacts = want.contacts ? contacts : []
  const empty = searchEmpty({
    query,
    loading,
    messageCount: shownMessages.length,
    contactCount: shownContacts.length,
  })

  const counts = {
    messages: chipCount({ enabled: want.messages, loaded: loadedMessages, n: messages.length }),
    contacts: chipCount({ enabled: want.contacts, loaded: loadedContacts, n: contacts.length }),
  }

  return (
    <div className="search" data-testid="search-page">
      <input
        className="search__input"
        data-testid="search-input"
        value={query}
        placeholder="Search messages and people"
        aria-label="Search messages and people"
        onChange={(e) => onQueryChange(e.target.value)}
      />

      <div className="search__chips" role="tablist" aria-label="Result type">
        {(['both', 'messages', 'contacts'] as const).map((t) => {
          const c = t === 'messages' ? counts.messages : t === 'contacts' ? counts.contacts : null
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={type === t}
              className={`search__chip${type === t ? ' search__chip--on' : ''}`}
              data-testid={`search-chip-${t}`}
              onClick={() => setType(t)}
            >
              {TYPE_LABEL[t]}
              {/* ⛔ A COUNT OR NOTHING. Never `0` before a query has run - that
                  says "none found" about a search nobody made. */}
              {c !== null && c.kind === 'known' && (
                <span className="search__count" data-testid={`search-count-${t}`}>
                  {c.n}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="search__facets" data-testid="search-facets">
        <label className="search__facet">
          <span>Channel</span>
          <select
            data-testid="facet-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="">Any</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="instagram">Instagram</option>
          </select>
        </label>

        <label className="search__facet">
          <span>Assignee</span>
          <select
            data-testid="facet-assignee"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value="">Anyone</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {`${u.firstName} ${u.lastName}`.trim()}
              </option>
            ))}
          </select>
        </label>

        {/* ⚠️ DATE DEFAULTS TO ALL TIME, which is `Build_Order:489`. Empty
            inputs send no predicate at all rather than a wide one. */}
        <label className="search__facet">
          <span>From</span>
          <input type="date" data-testid="facet-after" value={after} onChange={(e) => setAfter(e.target.value)} />
        </label>
        <label className="search__facet">
          <span>To</span>
          <input type="date" data-testid="facet-before" value={before} onChange={(e) => setBefore(e.target.value)} />
        </label>

        {/*
          ⛔ THE FOUR FACETS WITH NO STORAGE ARE NAMED, NOT RENDERED AS DISABLED
          CONTROLS. A chip that cannot filter is the empty-card failure item 11
          refuses; a sentence saying which phase owns them is a statement.
        */}
        <p className="search__gap" data-testid="search-facet-gap">
          {`Filtering by ${FACETS_AWAITING_CRM_BUILD.join(', ')} arrives with CRM-BUILD`}
        </p>
      </div>

      {failure !== null && (
        <p className="search__failure" role="alert" data-testid="search-failure">
          {failure}
        </p>
      )}

      {empty.kind !== 'hasResults' ? (
        <p className="search__empty" data-testid={`search-${empty.kind}`}>
          {empty.kind === 'idle'
            ? 'Search across every conversation'
            : empty.kind === 'tooShort'
              ? `Type at least ${MIN_QUERY} characters`
              : empty.kind === 'searching'
                ? 'Searching'
                : 'Nothing matches that'}
        </p>
      ) : (
        <ul className="search__results" data-testid="search-results">
          {shownMessages.map((m) => (
            <li key={m.messageId}>
              <button
                type="button"
                className="search__result"
                data-testid="search-result-message"
                /**
                 * ⛔ THE CONVERSATION AND THE MESSAGE. `Build_Order:490` wants
                 * the thread SCROLLED TO that message; dropping the message id
                 * opens the conversation at the bottom and leaves somebody to
                 * find it, which is the failure that line names.
                 */
                onClick={() => onOpenMessage(m.conversationId, m.messageId)}
              >
                <span className="search__who">{m.displayName ?? 'Unknown Contact'}</span>
                <span className="search__body">{m.body}</span>
              </button>
            </li>
          ))}
          {shownContacts.map((c) => {
            const target = contactTarget(c)
            return (
              <li key={c.contactId}>
                <button
                  type="button"
                  className="search__result"
                  data-testid="search-result-contact"
                  disabled={target.kind === 'none'}
                  onClick={() => {
                    if (target.kind === 'contact') onOpenConversation(target.conversationId)
                  }}
                >
                  <span className="search__who">{c.displayName ?? c.phoneNumber ?? 'Unknown Contact'}</span>
                  {target.kind === 'none' && (
                    <span className="search__meta" data-testid="search-no-thread">
                      No Conversation Yet
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/** Re-exported so `App.tsx` need not know the facet list to render the page. */
export { BUILDABLE_FACETS }
